import * as React from 'react';

// One flight of the copy tile per destination slot, looping. Geometry is in
// px within the 320x130 illustration stage.
const LOOP_SECONDS = 8;
const SLOT_COUNT = 5;
const LEAD_SECONDS = 0.5;
const TRAIL_SECONDS = 0.4;
const FLIGHT_START_X = 34;
const FLIGHT_END_X = 262;
const ARC_HEIGHT = 26;

// The clone request has no real progress events, so the bar approaches 95%
// asymptotically and parks there until the request settles and the modal
// switches state. The illustration loop keeps running throughout.
const PROGRESS_CAP = 95;
const PROGRESS_TAU_SECONDS = 4;

export interface FlyingTile {
  left: number;
  arc: number;
  rotation: number;
  opacity: number;
}

export interface CloneProgressFrame {
  stepIndex: number;
  progressPct: number;
  tile: FlyingTile;
  landedSlots: boolean[];
}

const clamp = (value: number, min: number, max: number): number =>
  Math.max(min, Math.min(max, value));

const easeInOut = (x: number): number => {
  const clamped = clamp(x, 0, 1);
  return clamped < 0.5 ? 2 * clamped * clamped : 1 - Math.pow(-2 * clamped + 2, 2) / 2;
};

export const useCloneProgress = (stepCount: number): CloneProgressFrame => {
  const [elapsed, setElapsed] = React.useState(0);

  React.useEffect(() => {
    const start = Date.now();
    const timer = window.setInterval(() => setElapsed((Date.now() - start) / 1000), 33);
    return () => window.clearInterval(timer);
  }, []);

  const t = elapsed % LOOP_SECONDS;
  const span = LOOP_SECONDS - LEAD_SECONDS - TRAIL_SECONDS;
  const segment = span / SLOT_COUNT;
  const flightDuration = segment * 0.72;

  const tile: FlyingTile = { left: FLIGHT_START_X, arc: 0, rotation: 0, opacity: 0 };
  const landedSlots: boolean[] = [];

  for (let i = 0; i < SLOT_COUNT; i++) {
    const windowStart = LEAD_SECONDS + i * segment;
    const p = (t - windowStart) / flightDuration;
    landedSlots[i] = t >= windowStart + flightDuration;
    if (p >= 0 && p <= 1) {
      const eased = easeInOut(p);
      tile.left = FLIGHT_START_X + (FLIGHT_END_X - FLIGHT_START_X) * eased;
      tile.opacity = clamp(Math.sin(p * Math.PI), 0, 1);
      tile.arc = -Math.sin(eased * Math.PI) * ARC_HEIGHT;
      tile.rotation = -8 + eased * 16;
    }
  }

  const progressPct = PROGRESS_CAP * (1 - Math.exp(-elapsed / PROGRESS_TAU_SECONDS));
  const stepIndex = Math.min(stepCount - 1, Math.floor((progressPct / PROGRESS_CAP) * stepCount));

  return { stepIndex, progressPct, tile, landedSlots };
};
